/* eslint-disable react/display-name, react-hooks/exhaustive-deps */
import { Fragment, createElement, useCallback } from "react"
import type { FC, ReactElement } from "react"

import eq from "lodash-es/eq"
import includes from "lodash-es/includes"

import { BetaMark } from "@/3514/components"
import { EFeedbackModalType, EFeedbackModalVariant } from "@/3514/modals"
import type { TEmptyCallback, TEmptyRenderCallback } from "@/shared/types/functions"

import { useCoachingChatContext as useChatContext, useCoachingChatContext as useContext } from "../context"
import { CoachingChatGuideModalOpener as ModalOpener } from "../modals/guide"
import { useCoachingChatConversationStatusSelector as useStatusSelector } from "../store"
import {
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode
} from "../types"

function withModeHeading<TProps extends object = { renderBetaMark: TEmptyRenderCallback }>(Heading: FC<TProps>): FC {
    return (): ReactElement => {
        const {
            entityMode,
            feedback: { handleModalOpen }
        }: ReturnType<typeof useContext> = useContext()

        const handleAction: TEmptyCallback = useCallback(
            (): void =>
                handleModalOpen({
                    variant: EFeedbackModalVariant.ChatGeneral,
                    type: EFeedbackModalType.General
                }),
            []
        )

        return createElement(Heading, {
            renderBetaMark: (): ReactElement =>
                eq(entityMode, EEntityMode.Light) ? createElement(Fragment) : createElement(BetaMark, { handleAction })
        } as TProps)
    }
}

function withModeInput(FullInput: FC, LightInput: FC): FC {
    return (): ReactElement => {
        const { entityId, entityMode }: ReturnType<typeof useContext> = useContext()

        const status: EConversationStatus = useStatusSelector(entityId)

        return eq(entityMode, EEntityMode.Light)
            ? includes([EConversationStatus.Completed], status)
                ? createElement(Fragment)
                : createElement(LightInput)
            : createElement(FullInput)
    }
}

function withModeInsights(Insights: FC): FC {
    return (): ReactElement => {
        const { entityMode }: ReturnType<typeof useContext> = useContext()

        return eq(entityMode, EEntityMode.Light) ? createElement(Fragment) : createElement(Insights)
    }
}

function withModeQuestion<TProps extends object = { index: number }>(
    FullQuestion: FC<TProps>,
    LightQuestion: FC<TProps>
): FC<TProps> {
    return (props: TProps): ReactElement => {
        const { entityMode }: ReturnType<typeof useContext> = useContext()

        return eq(entityMode, EEntityMode.Light)
            ? createElement(LightQuestion, props)
            : createElement(FullQuestion, props)
    }
}

function withModeQuestions<TProps extends object = { renderGuideModalOpener: TEmptyRenderCallback }>(
    QuestionList: FC<TProps>
): FC<TProps> {
    return (): ReactElement => {
        const { entityMode }: ReturnType<typeof useChatContext> = useChatContext()

        return createElement(QuestionList, {
            renderGuideModalOpener: (): ReactElement =>
                eq(entityMode, EEntityMode.Light) ? createElement(Fragment) : createElement(ModalOpener)
        } as TProps)
    }
}

function withModeMessage<TProps>(FullMessage: FC<TProps>, LightMessage: FC<TProps>): FC<TProps> {
    return (props: TProps): ReactElement => {
        const { entityMode }: ReturnType<typeof useContext> = useContext()

        return eq(entityMode, EEntityMode.Light)
            ? createElement(LightMessage, props)
            : createElement(FullMessage, props)
    }
}

function withModeMatrixFeedback(MatrixOpener: FC): FC {
    return (): ReactElement => {
        const { entityMode }: ReturnType<typeof useContext> = useContext()

        return eq(entityMode, EEntityMode.Light) ? createElement(Fragment) : createElement(MatrixOpener)
    }
}

const guards = {
    withModeHeading,
    withModeInput,
    withModeInsights,
    withModeQuestion,
    withModeQuestions,
    withModeMessage,
    withModeMatrixFeedback
}

export { guards as coachingChatComponentsGuards }
